import { db } from '../firebase/config';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  Timestamp,
  writeBatch
} from 'firebase/firestore';
import { Item } from '../models/Item';
import { ActivityLog } from '../models/ActivityLog';

class InventoryService {
  constructor() {
    this.itemsCollection = collection(db, 'items');
    this.activityLogCollection = collection(db, 'activity_log');
  }

  // Item operations
  async insertItem(item) {
    try {
      const itemData = item.toFirestore();
      const docRef = await addDoc(this.itemsCollection, itemData);

      // Log activity
      await this.logActivity(
        item.shopName,
        'CREATED',
        item.name,
        item.brand,
        item.category,
        `Created new item: ${item.name}`
      );

      return docRef.id;
    } catch (error) {
      console.error('Error inserting item:', error);
      throw error;
    }
  }

  async bulkInsertItems(items) {
    try {
      if (!items || items.length === 0) {
        throw new Error('No items to insert');
      }

      // Firestore batch write limit is 500 operations
      const batchSize = 500;
      const batches = [];
      let currentBatch = writeBatch(db);
      let operationCount = 0;

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        const itemData = item.toFirestore();
        const docRef = doc(this.itemsCollection);
        currentBatch.set(docRef, itemData);
        operationCount++;

        // If we've reached the batch limit or this is the last item, commit this batch
        if (operationCount >= batchSize || i === items.length - 1) {
          batches.push(currentBatch);
          if (i < items.length - 1) {
            currentBatch = writeBatch(db);
            operationCount = 0;
          }
        }
      }

      // Execute all batches
      for (const batch of batches) {
        await batch.commit();
      }

      // Log bulk activity
      const shopName = items[0]?.shopName || 'Unknown';
      await this.logActivity(
        shopName,
        'BULK_CREATED',
        '',
        '',
        '',
        `Bulk imported ${items.length} items`
      );

      return items.length;
    } catch (error) {
      console.error('Error bulk inserting items:', error);
      throw error;
    }
  }

  async updateItem(item) {
    try {
      if (!item.id) throw new Error('Item ID is required for update');

      const itemRef = doc(this.itemsCollection, item.id);
      const itemData = { ...item.toFirestore(), updatedAt: Timestamp.now() };

      await updateDoc(itemRef, itemData);

      // Log activity
      await this.logActivity(
        item.shopName,
        'UPDATED',
        item.name,
        item.brand,
        item.category,
        `Updated item: ${item.name}`
      );
    } catch (error) {
      console.error('Error updating item:', error);
      throw error;
    }
  }

  async deleteItem(item) {
    try {
      if (!item.id) throw new Error('Item ID is required for deletion');

      const shopName = item.shopName;
      const itemName = item.name;
      const itemBrand = item.brand;
      const itemCategory = item.category;

      await deleteDoc(doc(this.itemsCollection, item.id));

      // Log activity
      await this.logActivity(
        shopName,
        'DELETED',
        itemName,
        itemBrand,
        itemCategory,
        `Deleted item: ${itemName}`
      );
    } catch (error) {
      console.error('Error deleting item:', error);
      throw error;
    }
  }

  async sellItem(item, quantitySold, discountAmount = 0, sellingPrice = null) {
    try {
      // Ensure item is an Item instance
      const itemInstance = item instanceof Item ? item : new Item(item);

      if (itemInstance.quantity < quantitySold) {
        throw new Error('Insufficient stock');
      }

      if (!itemInstance.id) {
        throw new Error('Item ID is required for selling');
      }

      // Use provided selling price if item doesn't have one, otherwise use item's price
      const priceToUse = sellingPrice || itemInstance.sellingPrice || 0;
      if (priceToUse <= 0) {
        throw new Error('Selling price is required');
      }

      const saleAmount = priceToUse * quantitySold;
      const finalAmount = Math.max(0, saleAmount - (discountAmount || 0));

      // Create updated item with new quantity
      const updatedItem = new Item({
        ...itemInstance,
        quantity: itemInstance.quantity - quantitySold,
        updatedAt: Date.now()
      });

      await this.updateItem(updatedItem);

      // Log activity with discount information
      // Format matches Android app: "Sold X units of ItemName for ₱Amount (Discount: ₱Discount)"
      // Note: finalAmount is the amount after discount, which is what the customer actually paid
      let activityDetails = `Sold ${quantitySold} units of ${updatedItem.name} for ₱${finalAmount.toFixed(2)}`;
      if (discountAmount > 0) {
        activityDetails += ` (Discount: ₱${discountAmount.toFixed(2)})`;
      }

      // Log the activity - this should always succeed even if there's an error
      try {
        await this.logActivity(
          updatedItem.shopName,
          'SOLD',
          updatedItem.name,
          updatedItem.brand,
          updatedItem.category,
          activityDetails,
          discountAmount,
          quantitySold,
          itemInstance.originalCost,
          finalAmount
        );
      } catch (logError) {
        // Log error but don't fail the sale
        console.error('Error logging activity (sale still processed):', logError);
      }
    } catch (error) {
      console.error('Error selling item:', error);
      throw error;
    }
  }

  async getAllItems(shopName) {
    try {
      const q = query(
        this.itemsCollection,
        where('shopName', '==', shopName),
        orderBy('updatedAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => Item.fromFirestore(doc));
    } catch (error) {
      console.error('Error getting all items:', error);
      // Fallback query without orderBy
      try {
        const q = query(
          this.itemsCollection,
          where('shopName', '==', shopName)
        );
        const querySnapshot = await getDocs(q);
        const items = querySnapshot.docs.map(doc => Item.fromFirestore(doc));
        return items.sort((a, b) => b.updatedAt - a.updatedAt);
      } catch (fallbackError) {
        console.error('Fallback query also failed:', fallbackError);
        throw fallbackError;
      }
    }
  }

  async getPaginatedItems(shopName, limitCount = 12, lastDoc = null, category = null) {
    try {
      let constraints = [
        where('shopName', '==', shopName)
      ];
      
      if (category) {
        constraints.push(where('category', '==', category));
      }
      
      // orderBy requires a composite index if combining where('category') and orderBy('updatedAt')
      // To keep it simple and free-tier friendly without manually building indexes, we omit orderBy when categorizing.
      
      constraints.push(limit(limitCount));
      
      if (lastDoc) {
        constraints.push(startAfter(lastDoc));
      }
      
      const q = query(this.itemsCollection, ...constraints);
      const querySnapshot = await getDocs(q);
      
      const items = querySnapshot.docs.map(doc => Item.fromFirestore(doc));
      const newLastDoc = querySnapshot.docs.length > 0 ? querySnapshot.docs[querySnapshot.docs.length - 1] : null;
      
      return { items, lastDoc: newLastDoc };
    } catch (error) {
      console.error('Error getting paginated items:', error);
      throw error;
    }
  }

  async getItemById(shopName, id) {
    try {
      const docRef = doc(this.itemsCollection, id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists() && docSnap.data().shopName === shopName) {
        return Item.fromFirestore(docSnap);
      }
      return null;
    } catch (error) {
      console.error('Error getting item by ID:', error);
      throw error;
    }
  }

  async getLowStockItems(shopName, threshold = 5) {
    try {
      const q = query(
        this.itemsCollection,
        where('shopName', '==', shopName),
        where('quantity', '<=', threshold),
        orderBy('quantity', 'asc')
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => Item.fromFirestore(doc));
    } catch (error) {
      console.error('Error getting low stock items:', error);
      // Fallback query without orderBy
      try {
        const q = query(
          this.itemsCollection,
          where('shopName', '==', shopName),
          where('quantity', '<=', threshold)
        );
        const querySnapshot = await getDocs(q);
        const items = querySnapshot.docs.map(doc => Item.fromFirestore(doc));
        return items.sort((a, b) => a.quantity - b.quantity);
      } catch (fallbackError) {
        console.error('Fallback query also failed:', fallbackError);
        throw fallbackError;
      }
    }
  }

  async searchItems(shopName, searchTerm) {
    try {
      // Get all items for the shop and filter client-side
      const allItems = await this.getAllItems(shopName);
      const term = searchTerm.toLowerCase();

      return allItems.filter(item =>
        (item.brand && item.brand.toLowerCase().includes(term)) ||
        (item.name && item.name.toLowerCase().includes(term)) ||
        (item.category && item.category.toLowerCase().includes(term))
      );
    } catch (error) {
      console.error('Error searching items:', error);
      throw error;
    }
  }

  async getTotalItemCount(shopName) {
    try {
      const items = await this.getAllItems(shopName);
      return items.length;
    } catch (error) {
      console.error('Error getting total item count:', error);
      throw error;
    }
  }

  // Activity log operations
  async logActivity(shopName, action, itemName, itemBrand, itemCategory, details, discountAmount = 0, quantitySold = 0, itemCost = 0, saleAmount = 0) {
    try {
      const log = new ActivityLog({
        shopName,
        action,
        itemName,
        itemBrand,
        itemCategory,
        details,
        discountAmount: discountAmount || 0,
        quantitySold: quantitySold || 0,
        itemCost: itemCost || 0,
        saleAmount: saleAmount || 0
      });

      await addDoc(this.activityLogCollection, log.toFirestore());
    } catch (error) {
      console.error('Error logging activity:', error);
      // Don't throw error for logging failures
    }
  }

  async getAllActivities(shopName) {
    try {
      const q = query(
        this.activityLogCollection,
        where('shopName', '==', shopName),
        orderBy('timestamp', 'desc')
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ActivityLog.fromFirestore(doc));
    } catch (error) {
      console.error('Error getting all activities:', error);
      // Fallback query without orderBy
      try {
        const q = query(
          this.activityLogCollection,
          where('shopName', '==', shopName)
        );
        const querySnapshot = await getDocs(q);
        const activities = querySnapshot.docs.map(doc => ActivityLog.fromFirestore(doc));
        return activities.sort((a, b) => b.timestamp - a.timestamp);
      } catch (fallbackError) {
        console.error('Fallback query also failed:', fallbackError);
        throw fallbackError;
      }
    }
  }

  async getRecentActivities(shopName, limitCount = 10) {
    try {
      const q = query(
        this.activityLogCollection,
        where('shopName', '==', shopName),
        orderBy('timestamp', 'desc'),
        limit(limitCount)
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ActivityLog.fromFirestore(doc));
    } catch (error) {
      console.error('Error getting recent activities:', error);
      throw error;
    }
  }

  async getSalesActivities(shopName, startDate, endDate) {
    const startTimestamp = startDate.getTime();
    const endTimestamp = endDate.getTime();

    try {
      // Use getAllActivities which already has a robust fallback for missing indexes,
      // then filter for SOLD actions within the date range in memory
      const allActivities = await this.getAllActivities(shopName);

      return allActivities.filter(activity =>
        activity.action === 'SOLD' &&
        activity.timestamp >= startTimestamp &&
        activity.timestamp <= endTimestamp
      ).sort((a, b) => b.timestamp - a.timestamp);
    } catch (error) {
      console.error('Error getting sales activities:', error);
      return [];
    }
  }
}

export const inventoryService = new InventoryService();